import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  ExternalLink,
  HelpCircle,
  Hourglass,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  Button,
  NotificationCard,
  StatusBadge,
} from '@afrilink/ui';
import {
  useTransferOrder,
  type TransferOrderSnapshot,
  type TransferOrderJob,
} from './useTransferOrder';

/**
 * Customer-facing transfer status page. Lives at
 * `/app/domains/transfer/:order_id` and renders the lifecycle timeline
 * for one transfer-in order.
 *
 * Auto-refreshes every 60s while tab is visible (locked G3-Q7); pauses
 * on hidden via Page Visibility API. Stops polling at terminal status.
 *
 * Status copy mapping per BRD §5.3.
 */

type Tone = 'pending' | 'progress' | 'complete' | 'failed';

interface TimelineEntry {
  key: string;
  title: string;
  body: string;
  tone: Tone;
  at?: string | null;
  icon: typeof Clock;
}

const SUPPORT_URL = '/app/support/new';

export default function TransferStatusPage() {
  const navigate = useNavigate();
  const { order_id: orderId } = useParams();
  const { data, loading, error, refetch, isTerminal } = useTransferOrder(orderId);

  const timeline = useMemo(() => (data ? buildTimeline(data) : []), [data]);

  return (
    <UniversalPage
      title={data ? `Transfer · ${data.order.domain_name}` : 'Transfer status'}
      description="Track an in-flight domain transfer-in."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Domains', href: '/app/domains' },
        { label: 'Transfer status' },
      ]}
      actions={
        data && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(
                `${SUPPORT_URL}?subject=${encodeURIComponent(
                  `Help with transfer of ${data.order.domain_name}`,
                )}&body=${encodeURIComponent(
                  `Order ID: ${data.order.id}\nDomain: ${data.order.domain_name}\nStatus: ${data.order.status}`,
                )}`,
              )
            }
          >
            <HelpCircle className="size-4" />
            Need help?
          </Button>
        )
      }
    >
      {loading && !data && (
        <Card interactive={false}>
          <UniversalLoadingState variant="spinner" caption="Loading transfer status…" />
        </Card>
      )}

      {error && !data && (
        <NotificationCard
          tone="danger"
          title="Could not load transfer"
          body={
            <>
              {error}
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/app/domains')}>
                  Back to domains
                </Button>
                <Button variant="outline" size="sm" onClick={() => void refetch()}>
                  Try again
                </Button>
              </div>
            </>
          }
        />
      )}

      {data && (
        <div className="space-y-6">
          <HeaderCard data={data} isTerminal={isTerminal} onPay={() => data.invoice && navigate(`/app/billing/invoices/${data.invoice.id}`)} />

          <Card interactive={false}>
            <header className="border-b border-border px-6 py-4">
              <h3 className="text-sm font-semibold text-navy-primary">Transfer timeline</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                As of {formatDateTime(data.fetchedAt)} · refreshes automatically every 60 seconds.
              </p>
            </header>
            <ol className="space-y-0">
              {timeline.map((entry, idx) => (
                <TimelineRow key={entry.key} entry={entry} isLast={idx === timeline.length - 1} />
              ))}
            </ol>
          </Card>

          {data.order.last_error && (
            <NotificationCard
              tone="warning"
              title="Last registrar error"
              body={data.order.last_error}
            />
          )}
        </div>
      )}
    </UniversalPage>
  );
}

// ─── Header card ──────────────────────────────────────────────────────

function HeaderCard({
  data,
  isTerminal,
  onPay,
}: {
  data: TransferOrderSnapshot;
  isTerminal: boolean;
  onPay: () => void;
}) {
  const status = data.order.status;
  const needsPayment = data.invoice && data.invoice.lifecycle_state !== 'paid' && status === 'pending_payment';

  let badge: { tone: 'pending' | 'active' | 'failed' | 'info'; label: string };
  if (status === 'complete') badge = { tone: 'active', label: 'Complete' };
  else if (status.startsWith('failed')) badge = { tone: 'failed', label: 'Failed' };
  else if (status === 'pending_payment') badge = { tone: 'pending', label: 'Awaiting payment' };
  else if (status === 'polling') badge = { tone: 'info', label: 'Awaiting registry' };
  else badge = { tone: 'info', label: status };

  return (
    <Card interactive={false}>
      <div className="flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Transferring in</p>
          <p className="text-xl font-semibold tabular-nums text-navy-primary">{data.order.domain_name}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Order started {formatDate(data.order.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
          {needsPayment && (
            <Button onClick={onPay}>
              <CreditCard className="size-4" />
              Pay invoice
            </Button>
          )}
          {isTerminal && status === 'complete' && (
            <Button variant="outline" onClick={() => (window.location.href = `/app/domains/${data.order.domain_name}`)}>
              <ArrowRight className="size-4" />
              Open domain
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Timeline row ─────────────────────────────────────────────────────

const TONE_STYLES: Record<Tone, { bg: string; ring: string; icon: string }> = {
  pending:  { bg: 'bg-muted', ring: 'ring-border', icon: 'text-muted-foreground' },
  progress: { bg: 'bg-warning/10',    ring: 'ring-warning/30',   icon: 'text-warning-dark' },
  complete: { bg: 'bg-success/10',  ring: 'ring-success/30',  icon: 'text-success' },
  failed:   { bg: 'bg-danger/10',   ring: 'ring-danger/30',   icon: 'text-danger' },
};

function TimelineRow({ entry, isLast }: { entry: TimelineEntry; isLast: boolean }) {
  const tone = TONE_STYLES[entry.tone];
  const Icon = entry.icon;
  return (
    <li className="flex gap-4 px-6 py-4">
      <div className="flex flex-col items-center">
        <span className={`flex h-8 w-8 items-center justify-center rounded-full ${tone.bg} ring-2 ${tone.ring}`}>
          <Icon className={`size-4 ${tone.icon}`} />
        </span>
        {!isLast && <span className="mt-1 w-px flex-1 bg-muted" aria-hidden />}
      </div>
      <div className="flex-1 pb-2">
        <p className="text-sm font-semibold text-navy-primary">{entry.title}</p>
        <p className="mt-0.5 text-sm text-foreground-soft">{entry.body}</p>
        {entry.at && (
          <p className="mt-1 text-xs text-muted-foreground tabular-nums">{formatDateTime(entry.at)}</p>
        )}
      </div>
    </li>
  );
}

// ─── Timeline composition ─────────────────────────────────────────────

function buildTimeline(snap: TransferOrderSnapshot): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  const order = snap.order;
  const invoice = snap.invoice;

  // 1. Order created
  entries.push({
    key: 'order_created',
    title: 'Transfer requested',
    body: `Your transfer-in for ${order.domain_name} was created.`,
    tone: 'complete',
    at: order.created_at,
    icon: CheckCircle2,
  });

  // 2. Invoice issued + payment
  if (invoice) {
    if (invoice.lifecycle_state === 'paid') {
      entries.push({
        key: 'paid',
        title: 'Payment received',
        body: `Invoice ${invoice.number} paid. We've started the transfer at the registrar.`,
        tone: 'complete',
        icon: CheckCircle2,
      });
    } else {
      entries.push({
        key: 'awaiting_payment',
        title: 'Pay invoice to start the transfer',
        body: `Invoice ${invoice.number} for R${invoice.total.toFixed(2)} is waiting. The transfer doesn't start at the registrar until this is paid.`,
        tone: 'progress',
        icon: CreditCard,
      });
    }
  }

  // 3. Provisioning jobs — registrar request + polling
  const orderedJobs = [...snap.jobs].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const initiatorJob = orderedJobs.find((j) => !(j.payload as { transfer_initiated?: boolean })?.transfer_initiated);
  const pollJobs = orderedJobs.filter((j) => (j.payload as { transfer_initiated?: boolean })?.transfer_initiated);

  if (initiatorJob) {
    if (initiatorJob.status === 'complete') {
      entries.push({
        key: 'registrar_initiated',
        title: 'Registrar received the request',
        body: 'We sent the transfer request to the registrar. The losing registrar typically has 5–7 days to release the domain.',
        tone: 'complete',
        at: initiatorJob.finished_at,
        icon: CheckCircle2,
      });
    } else if (initiatorJob.status === 'failed_terminal') {
      entries.push({
        key: 'registrar_failed',
        title: 'Registrar rejected the request',
        body: registrarFailureCopy(initiatorJob),
        tone: 'failed',
        at: initiatorJob.finished_at,
        icon: XCircle,
      });
    } else if (initiatorJob.status === 'failed_retryable') {
      entries.push({
        key: 'registrar_retrying',
        title: 'Hit a temporary issue — retrying',
        body: 'We had trouble reaching the registrar. We\'ll automatically retry shortly.',
        tone: 'progress',
        at: initiatorJob.scheduled_for,
        icon: AlertCircle,
      });
    } else {
      entries.push({
        key: 'registrar_running',
        title: 'Sending request to registrar',
        body: 'We\'re submitting the transfer request now.',
        tone: 'progress',
        at: initiatorJob.started_at ?? initiatorJob.created_at,
        icon: Hourglass,
      });
    }
  }

  // Polling jobs — collapse into one "polling" row showing latest state
  const latestPoll = pollJobs[pollJobs.length - 1];
  if (latestPoll) {
    if (latestPoll.status === 'complete' && order.status === 'complete') {
      entries.push({
        key: 'transfer_complete',
        title: 'Transfer complete',
        body: 'The domain is now in your SightHost portal.',
        tone: 'complete',
        at: latestPoll.finished_at,
        icon: CheckCircle2,
      });
    } else if (latestPoll.status === 'failed_terminal' || order.status.startsWith('failed')) {
      entries.push({
        key: 'transfer_failed',
        title: 'Transfer could not complete',
        body: latestPoll.error_message ?? 'The losing registrar refused or the registry rejected the transfer.',
        tone: 'failed',
        at: latestPoll.finished_at,
        icon: XCircle,
      });
    } else {
      const nextAt = latestPoll.scheduled_for;
      entries.push({
        key: 'polling',
        title: 'Awaiting registry confirmation',
        body: `Most .za transfers complete in 5–7 days. ${
          nextAt ? `We'll check again at ${formatDateTime(nextAt)}.` : ''
        }`.trim(),
        tone: 'progress',
        at: latestPoll.created_at,
        icon: Hourglass,
      });
    }
  } else if (initiatorJob && initiatorJob.status === 'complete' && order.status === 'polling') {
    // Initiator finished, no poll yet (race window)
    entries.push({
      key: 'polling_pending',
      title: 'Awaiting first registrar check',
      body: 'We\'ll check transfer status with the registrar shortly.',
      tone: 'progress',
      at: initiatorJob.finished_at,
      icon: Hourglass,
    });
  }

  // 4. Final state when terminal but no jobs (edge case)
  if (order.status === 'complete' && !entries.some((e) => e.key === 'transfer_complete')) {
    entries.push({
      key: 'transfer_complete',
      title: 'Transfer complete',
      body: 'The domain is now in your SightHost portal.',
      tone: 'complete',
      at: order.completed_at,
      icon: CheckCircle2,
    });
  }
  if (order.status.startsWith('failed') && !entries.some((e) => e.tone === 'failed')) {
    entries.push({
      key: 'order_failed',
      title: 'Transfer failed',
      body: order.last_error ?? 'The transfer could not complete. Open a support ticket and we\'ll help.',
      tone: 'failed',
      icon: ShieldAlert,
    });
  }

  return entries;
}

function registrarFailureCopy(job: TransferOrderJob): string {
  const msg = (job.error_message ?? '').toLowerCase();
  if (msg.includes('epp') || msg.includes('auth')) {
    return 'The auth code didn\'t work. Open a support ticket and we\'ll help you reset it.';
  }
  if (msg.includes('lock') || msg.includes('locked')) {
    return 'The domain is still locked at the previous registrar. Unlock it there, then we can retry.';
  }
  if (msg.includes('60 day') || msg.includes('newly')) {
    return 'The registry rejected the transfer because the domain is too newly registered or transferred (60-day rule).';
  }
  return job.error_message ?? 'The registrar rejected the transfer request.';
}

// ─── Date formatters ──────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-ZA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Re-export for type/icon import roundtripping where needed elsewhere.
export type { TimelineEntry };
